import React, { useState } from 'react';
import { Database } from 'lucide-react';
import { AssetsTable } from '../components/assets/AssetsTable';
import { AssetsFilters } from '../components/assets/AssetsFilters';
import { AssetsMetrics } from '../components/assets/AssetsMetrics';
import { AssetTabs } from '../components/assets/AssetTabs';
import { ExportButton, AddAssetButton } from '../components/assets/AssetActionButtons';
import { assets, metrics } from '../data/assets';

const ITEMS_PER_PAGE = 10;

const AllAssets = () => {
  // Table selection state
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);

  const handleExportCSV = () => {
    const headers = ['ID', 'Name', 'Type', 'Status', 'Region', 'Last Scan'];
    const csvContent = assets.map(asset => [
      asset.id,
      asset.name,
      asset.type,
      asset.status,
      asset.region,
      asset.updatedAt
    ].join(','));

    const csv = [headers.join(','), ...csvContent].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'assets.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSelectAll = (selected: boolean) => {
    setSelectedAssets(selected ? assets.map(a => a.id) : []);
  };

  const handleSelectAsset = (id: string, selected: boolean) => {
    if (selected) {
      setSelectedAssets(prev => [...prev, id]);
    } else {
      setSelectedAssets(prev => prev.filter(aid => aid !== id));
    }
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedType('all');
    setSelectedStatus('all');
    setSelectedTags([]);
    setSelectedRegions([]);
  };

  const handleTagChange = (tag: string, checked: boolean) => {
    setSelectedTags(prev =>
      checked ? [...prev, tag] : prev.filter(t => t !== tag)
    );
  };

  const handleRegionChange = (region: string, checked: boolean) => {
    setSelectedRegions(prev =>
      checked ? [...prev, region] : prev.filter(r => r !== region)
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Database className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Assets</h1>
            <p className="text-sm text-gray-400">Manage and monitor your digital assets</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <ExportButton onClick={handleExportCSV} />
          <AddAssetButton onClick={() => console.log('Adding new asset...')} />
        </div>
      </div>

      {/* Metrics */}
      <AssetsMetrics metrics={metrics} />

      {/* Tabs */}
      <AssetTabs />

      {/* Filters */}
      <AssetsFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedType={selectedType}
        onTypeChange={setSelectedType}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        selectedTags={selectedTags}
        onTagChange={handleTagChange}
        selectedRegions={selectedRegions}
        onRegionChange={handleRegionChange}
        onReset={handleResetFilters}
        onExport={handleExportCSV}
      />

      {/* Assets Table */}
      <AssetsTable
        assets={assets.map(asset => ({
          ...asset,
          type: asset.type as 'web' | 'database' | 'server' | 'network',
          status: asset.status as 'active' | 'inactive' | 'maintenance' | 'vulnerable',
          vulnerabilities: {
            ...asset.vulnerabilities,
            total: Object.values(asset.vulnerabilities).reduce((sum, count) => sum + count, 0)
          }
        }))}
        // selectedAssets={selectedAssets}
        // onSelectAll={handleSelectAll}
        // onSelectAsset={handleSelectAsset}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalAssets={assets.length}
        itemsPerPage={ITEMS_PER_PAGE}
      />
    </div>
  );
};

export default AllAssets;